import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { FaWallet, FaSync, FaSearch, FaCalendarAlt, FaPlus, FaTimes, FaCreditCard, FaCheckCircle, FaFileInvoiceDollar, FaArrowUp, FaClipboardList, FaClock } from "react-icons/fa";
import { api } from "../utils/apiUtils";
import socketService from "../utils/socketService";
import axios from "axios";

const { API_URL } = require("../config/apiConfig");

const CorporateWallet = () => {
  const [loading, setLoading] = useState(false);
  const [walletLoading, setWalletLoading] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const pollingIntervalRef = useRef(null);
  const socketConnectedRef = useRef(false);

  const [filters, setFilters] = useState({
    type: "all",
    status: "all",
    bookingId: "",
    from: "",
    to: "",
  });

  // Quick topup state
  const [showQuickTopup, setShowQuickTopup] = useState(false);
  const [quickTopupAmount, setQuickTopupAmount] = useState("");
  const [topupLoading, setTopupLoading] = useState(false);

  // Credit request state
  const [showRequestCredit, setShowRequestCredit] = useState(false);
  const [creditRequests, setCreditRequests] = useState([]);
  const [creditRequestAmount, setCreditRequestAmount] = useState("");
  const [creditRequestReason, setCreditRequestReason] = useState("");
  const [requestLoading, setRequestLoading] = useState(false);
  const filtersRef = useRef(filters);

  // Update filters ref whenever filters change
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const fetchWallet = async () => {
    setWalletLoading(true);
    try {
      console.log('Fetching wallet data...');
      const res = await api.get("/corporate/wallet");
      console.log('Wallet response:', res?.data);
      if (res?.data?.success && res?.data?.data) {
        console.log('Wallet data updated:', res.data.data);
        setWallet(res.data.data);
      } else {
        console.warn('Invalid wallet response:', res?.data);
        setWallet(null);
      }
    } catch (e) {
      console.error('Error fetching wallet:', e?.message);
      setWallet(null);
      // Don't show error toast if it's a background refresh
      if (typeof e?.response?.status === 'undefined' || e.response.status >= 500) {
        toast.error(e?.response?.data?.message || e.message || "Failed to load wallet");
      }
    } finally {
      setWalletLoading(false);
    }
  };

  const fetchTransactions = async (filtersOverride = null) => {
    setLoading(true);
    try {
      const currentFilters = filtersOverride || filters;
      const params = new URLSearchParams();
      if (currentFilters.type && currentFilters.type !== "all") params.set("type", currentFilters.type);
      if (currentFilters.status && currentFilters.status !== "all") params.set("status", currentFilters.status);
      if (currentFilters.bookingId) params.set("bookingId", currentFilters.bookingId);
      if (currentFilters.from) params.set("from", currentFilters.from);
      if (currentFilters.to) params.set("to", currentFilters.to);

      const url = params.toString()
        ? `/corporate/wallet/transactions?${params.toString()}`
        : "/corporate/wallet/transactions";

      const res = await api.get(url);
      if (res?.data?.success) {
        setTransactions(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setTransactions([]);
      }
    } catch (e) {
      setTransactions([]);
      console.error("Failed to load transactions:", e?.message);
      // Don't show error toast for background refreshes
      if (!filtersOverride) {
        toast.error(e?.response?.data?.message || e.message || "Failed to load transactions");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditRequests = async () => {
    try {
      const res = await api.get("/corporate/wallet/credit-requests");
      if (res?.data?.success) {
        setCreditRequests(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setCreditRequests([]);
      }
    } catch (e) {
      setCreditRequests([]);
      console.error("Failed to load credit requests:", e.message);
    }
  };

  useEffect(() => {
    // Load Razorpay script
    const loadRazorpay = () => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => {
        console.log("Razorpay script loaded");
      };
      script.onerror = () => {
        console.error("Failed to load Razorpay script");
      };
      document.head.appendChild(script);
    };

    loadRazorpay();

    // Initial fetch
    fetchWallet();
    fetchTransactions();
    fetchCreditRequests();

    // Setup socket connection for real-time updates
    try {
      socketService.connect();

      // Get corporate account ID from wallet data or from API
      const getCorporateAccountId = async () => {
        try {
          const res = await api.get("/corporate/wallet");
          if (res?.data?.data?.corporateAccountId) {
            const accountId = res.data.data.corporateAccountId;
            // Wait for socket to be connected before joining room
            const checkConnection = setInterval(() => {
              const isConnected = socketService.socket?.connected || socketService.isConnected;
              if (isConnected) {
                clearInterval(checkConnection);
                socketService.joinCorporateRoom(accountId);
                socketConnectedRef.current = true;
                console.log(`Joined corporate room for account ${accountId}`);
              }
            }, 100);
            // Timeout after 10 seconds
            setTimeout(() => clearInterval(checkConnection), 10000);
          }
        } catch (error) {
          console.error('Failed to get corporate account ID:', error);
        }
      };

      getCorporateAccountId();

      // Listen for wallet updates from socket
      socketService.listenForEvent('walletUpdated', (data) => {
        console.log('Received wallet update via socket:', data);
        // Refresh wallet and transactions on socket update
        fetchWallet();
        fetchTransactions(filtersRef.current);
      });

      socketService.listenForEvent('commissionProcessed', (data) => {
        console.log('Received commission update via socket:', data);
        fetchWallet();
        fetchTransactions(filtersRef.current);
      });
    } catch (error) {
      console.error('Socket connection setup failed:', error);
    }

    // Setup polling as fallback (every 30 seconds)
    pollingIntervalRef.current = setInterval(() => {
      console.log('Polling for wallet updates...');
      fetchWallet();
      // Only refresh transactions if there are filters, otherwise it's less important
      if (filters.bookingId || filters.from || filters.to) {
        fetchTransactions();
      }
    }, 30000);

    // Cleanup
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      // Note: We don't disconnect socket here as it's shared across pages
      // socketService.disconnect();
    };
  }, []);

  const refreshAll = async () => {
    await Promise.all([fetchWallet(), fetchTransactions()]);
  };

  const handleRazorpayPayment = async (amount) => {
    try {
      if (!window.Razorpay) {
        toast.error("Payment gateway is not loaded. Please refresh the page.");
        setTopupLoading(false);
        return;
      }

      // Create Razorpay order via backend
      const orderResponse = await axios.post(
        `${API_URL}/payment/wallet/create-topup-order`,
        { amount, currency: "INR" },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("corporateToken")}`,
          },
        }
      );

      if (!orderResponse?.data?.order?.id) {
        toast.error("Failed to create payment order");
        setTopupLoading(false);
        return;
      }

      const options = {
        key: orderResponse.data.key || process.env.REACT_APP_RAZORPAY_KEY || process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_test_PnSgiqE2elzEYx",
        amount: amount * 100,
        currency: "INR",
        order_id: orderResponse.data.order.id,
        handler: async (response) => {
          try {
            // Verify payment with backend
            const verifyResponse = await axios.post(
              `${API_URL}/payment/wallet/verify-topup`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                amount: amount,
              },
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("corporateToken")}`,
                },
              }
            );

            if (verifyResponse?.data?.success) {
              toast.success(`Successfully added ₹${amount.toLocaleString("en-IN")} to wallet`);
              setShowQuickTopup(false);
              setQuickTopupAmount("");
              // Wait a moment for the backend to fully commit the transaction, then refresh
              await new Promise(resolve => setTimeout(resolve, 800));
              console.log('Refreshing wallet data after topup...');
              await fetchWallet();
              await fetchTransactions();
              // Ensure state is updated by waiting a bit more
              await new Promise(resolve => setTimeout(resolve, 300));
              toast.info("Wallet updated successfully");
            }
          } catch (error) {
            const errorMsg = error?.response?.data?.message || "Payment verification failed";
            toast.error(errorMsg);
          } finally {
            setTopupLoading(false);
          }
        },
        prefill: {
          contact: "9942500999",
        },
        theme: { color: "#10B981" },
        modal: {
          ondismiss: function () {
            setTopupLoading(false);
            toast.info("Payment cancelled");
          },
        },
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.open();
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error.message || "Failed to initiate payment";
      toast.error(errorMsg);
      setTopupLoading(false);
    }
  };

  const handleQuickTopup = async (e) => {
    e.preventDefault();

    const amount = parseFloat(quickTopupAmount);
    if (!amount || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setTopupLoading(true);
    await handleRazorpayPayment(amount);
  };

  const handleRequestCredit = async (e) => {
    e.preventDefault();

    const amount = parseFloat(creditRequestAmount);
    if (!amount || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (!creditRequestReason.trim()) {
      toast.error("Please provide a reason for the credit request");
      return;
    }

    setRequestLoading(true);
    try {
      const res = await api.post("/corporate/wallet/request-credit", {
        amount,
        reason: creditRequestReason,
      });

      if (res?.data?.success) {
        toast.success("Credit request submitted successfully. Awaiting admin approval.");
        setShowRequestCredit(false);
        setCreditRequestAmount("");
        setCreditRequestReason("");
        await fetchCreditRequests();
      } else {
        toast.error(res?.data?.message || "Failed to submit credit request");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || "Failed to submit credit request");
    } finally {
      setRequestLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(amount || 0));
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const onSearch = async (e) => {
    e.preventDefault();
    await fetchTransactions();
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8 bg-gradient-to-r from-slate-50 to-red-50/20 p-6 rounded-2xl border border-slate-100">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#F56753]/10 flex items-center justify-center text-[#F56753] shadow-inner">
            <FaWallet className="text-xl" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-800 tracking-tight flex items-center gap-2">
              Corporate Wallet
              {wallet?.walletStatus && (
                <span className={`text-[10px] uppercase px-2.5 py-0.5 rounded-full font-bold tracking-wider shadow-sm ${
                  wallet.walletStatus === 'active' 
                    ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' 
                    : 'bg-rose-50 text-rose-600 border border-rose-200'
                }`}>
                  {wallet.walletStatus}
                </span>
              )}
            </h1>
            <p className="text-xs text-slate-500 font-semibold mt-0.5">Manage billing, credit limits, top-ups, and transaction histories</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap w-full md:w-auto">
          <button
            onClick={() => setShowQuickTopup(true)}
            disabled={loading || walletLoading || topupLoading}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl font-bold text-sm hover:bg-emerald-700 active:scale-95 transition-all shadow-lg shadow-emerald-600/10 disabled:opacity-50"
          >
            <FaPlus className="text-xs" /> Add Funds
          </button>
          <button
            onClick={() => setShowRequestCredit(true)}
            disabled={loading || walletLoading || requestLoading}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 px-5 py-2.5 bg-[#F56753] text-white rounded-xl font-bold text-sm hover:bg-[#d94e3a] active:scale-95 transition-all shadow-lg shadow-red-600/10 disabled:opacity-50"
          >
            <FaPlus className="text-xs" /> Request Credit
          </button>
          <button
            onClick={refreshAll}
            disabled={loading || walletLoading}
            className="flex items-center justify-center p-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl active:scale-95 transition-all disabled:opacity-50"
            title="Refresh Data"
          >
            <FaSync className={`${(loading || walletLoading) ? "animate-spin" : ""} text-sm`} />
          </button>
        </div>
      </div>

      {/* Pending Credit Requests */}
      {creditRequests.some(r => r.status === 'pending') && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50/50 border border-amber-100 rounded-2xl p-5 mb-8 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-600 mt-0.5">
              <FaClock className="text-lg animate-pulse" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-amber-900 mb-1 text-sm tracking-wide uppercase">Pending Credit Requests</h3>
              <p className="text-xs text-amber-700 mb-4">The following requests are currently awaiting administrator review:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {creditRequests.filter(r => r.status === 'pending').map(req => (
                  <div key={req._id} className="bg-white border border-amber-100/50 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-amber-500/5 rounded-full -translate-y-6 translate-x-6 group-hover:scale-125 transition-transform duration-500"></div>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="text-xs text-slate-400 font-bold block mb-0.5">Code: {req.requestCode}</span>
                        <span className="text-lg font-black text-slate-800">{formatCurrency(req.requestedAmount)}</span>
                      </div>
                      <span className="text-[9px] bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-bold tracking-wider">
                        PENDING
                      </span>
                    </div>
                    {req.reason && (
                      <p className="text-xs text-slate-600 bg-slate-50 p-2.5 rounded-lg border border-slate-100 font-medium italic mt-2">
                        "{req.reason}"
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {wallet?.billingModel === 'wallet' ? (
          <>
            {/* Wallet Model Card 1: Wallet Balance */}
            <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 to-red-950 text-white rounded-2xl p-6 shadow-xl hover:-translate-y-1 transition-transform duration-300">
              <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-[#F56753]/10 rounded-full blur-xl"></div>
              <div className="flex justify-between items-start mb-4">
                <div className="text-xs font-bold uppercase tracking-widest text-red-300">Wallet Balance</div>
                <FaWallet className="text-red-400 text-lg opacity-80" />
              </div>
              <div className="text-3xl font-black tracking-tight mb-2">
                {formatCurrency(wallet?.walletBalance)}
              </div>
              <div className="text-[10px] text-red-200 font-bold bg-white/10 px-2 py-1 rounded w-max uppercase tracking-wider">
                Status: {(wallet?.walletStatus || "-")}
              </div>
            </div>

            {/* Wallet Model Card 2: Available Credit */}
            <div className="relative overflow-hidden bg-gradient-to-br from-[#F56753] to-red-900 text-white rounded-2xl p-6 shadow-xl hover:-translate-y-1 transition-transform duration-300">
              <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
              <div className="flex justify-between items-start mb-4">
                <div className="text-xs font-bold uppercase tracking-widest text-red-200">Available Credit</div>
                <FaCreditCard className="text-red-200 text-lg opacity-80" />
              </div>
              <div className="text-3xl font-black tracking-tight mb-2">
                {formatCurrency(wallet?.availableCredit)}
              </div>
              <div className="text-xs text-red-100/80 font-medium">
                Granted from credit limit
              </div>
            </div>

            {/* Wallet Model Card 3: Total Spendable */}
            <div className="relative overflow-hidden bg-gradient-to-br from-emerald-600 to-teal-800 text-white rounded-2xl p-6 shadow-xl hover:-translate-y-1 transition-transform duration-300">
              <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
              <div className="flex justify-between items-start mb-4">
                <div className="text-xs font-bold uppercase tracking-widest text-emerald-200">Total Available</div>
                <FaCheckCircle className="text-emerald-200 text-lg opacity-80" />
              </div>
              <div className="text-3xl font-black tracking-tight mb-2">
                {formatCurrency(wallet?.totalAvailable)}
              </div>
              <div className="text-xs text-emerald-100/80 font-medium">
                Ready to use for booking rides
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Credit Model Card 1: Credit Limit */}
            <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-2xl p-6 shadow-xl hover:-translate-y-1 transition-transform duration-300">
              <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-slate-500/10 rounded-full blur-xl"></div>
              <div className="flex justify-between items-start mb-4">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-400">Credit Limit</div>
                <FaFileInvoiceDollar className="text-slate-400 text-lg opacity-80" />
              </div>
              <div className="text-3xl font-black tracking-tight mb-2">
                {formatCurrency(wallet?.creditLimit)}
              </div>
              <div className="text-xs text-slate-400 font-semibold">
                Maximum credit authorization
              </div>
            </div>

            {/* Credit Model Card 2: Spent / Credit Used */}
            <div className="relative overflow-hidden bg-white text-slate-800 border border-slate-100 rounded-2xl p-6 shadow-lg hover:-translate-y-1 transition-transform duration-300">
              <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-rose-50 rounded-full blur-xl opacity-60"></div>
              <div className="flex justify-between items-start mb-4">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-400">Credit Used</div>
                <div className="w-8 h-8 rounded-full bg-rose-50 flex items-center justify-center text-rose-600 font-bold">
                  <FaArrowUp className="text-sm" />
                </div>
              </div>
              <div className="text-3xl font-black tracking-tight text-slate-800 mb-2">
                {formatCurrency((wallet?.creditUsed || 0) + (wallet?.reservedFunds || 0))}
              </div>
              <div className="text-xs text-slate-500 font-semibold flex items-center gap-1">
                Includes {formatCurrency(wallet?.reservedFunds || 0)} reserved for active rides
              </div>
            </div>

            {/* Credit Model Card 3: Spendable Balance */}
            <div className="relative overflow-hidden bg-gradient-to-br from-emerald-600 to-teal-700 text-white rounded-2xl p-6 shadow-xl hover:-translate-y-1 transition-transform duration-300">
              <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
              <div className="flex justify-between items-start mb-4">
                <div className="text-xs font-bold uppercase tracking-widest text-emerald-200">Available Credit</div>
                <FaCheckCircle className="text-emerald-200 text-lg opacity-80" />
              </div>
              <div className="text-3xl font-black tracking-tight mb-2">
                {formatCurrency((wallet?.availableCredit || 0) + (wallet?.walletBalance || 0))}
              </div>
              <div className="text-xs text-emerald-100/80 font-semibold">
                Spendable credit + top-ups
              </div>
            </div>
          </>
        )}
      </div>

      {/* Transactions Section */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-xl shadow-slate-100/50 mb-8 overflow-hidden animate-fadeIn">
        <div className="p-6 border-b border-slate-100 bg-gradient-to-b from-slate-50/50 to-white">
          <div className="flex items-center gap-2 mb-6">
            <FaClipboardList className="text-[#F56753] text-lg" />
            <h2 className="text-lg font-black text-slate-800">Transaction History</h2>
          </div>

          <form onSubmit={onSearch} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 items-end">
            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5 px-1">Type</label>
              <select
                value={filters.type}
                onChange={(e) => setFilters((p) => ({ ...p, type: e.target.value }))}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 font-bold focus:outline-none focus:ring-2 focus:ring-[#F56753] focus:bg-white transition-all cursor-pointer"
              >
                <option value="all">All Types</option>
                <option value="WALLET_TOPUP">WALLET_TOPUP</option>
                <option value="RIDE_DEBIT">RIDE_DEBIT</option>
                <option value="RIDE_CREDIT">RIDE_CREDIT</option>
                <option value="REFUND">REFUND</option>
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5 px-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 font-bold focus:outline-none focus:ring-2 focus:ring-[#F56753] focus:bg-white transition-all cursor-pointer"
              >
                <option value="all">All Statuses</option>
                <option value="SUCCESS">SUCCESS</option>
                <option value="FAILED">FAILED</option>
                <option value="PENDING">PENDING</option>
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5 px-1">Booking ID</label>
              <input
                value={filters.bookingId}
                onChange={(e) => setFilters((p) => ({ ...p, bookingId: e.target.value }))}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 font-bold placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#F56753] focus:bg-white transition-all"
                placeholder="e.g. CB260521A7Q4"
              />
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5 px-1">From Date</label>
              <div className="relative">
                <FaCalendarAlt className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                <input
                  type="date"
                  value={filters.from}
                  onChange={(e) => setFilters((p) => ({ ...p, from: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3 py-2 text-sm text-slate-700 font-bold focus:outline-none focus:ring-2 focus:ring-[#F56753] focus:bg-white transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5 px-1">To Date</label>
              <div className="relative">
                <FaCalendarAlt className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                <input
                  type="date"
                  value={filters.to}
                  onChange={(e) => setFilters((p) => ({ ...p, to: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3 py-2 text-sm text-slate-700 font-bold focus:outline-none focus:ring-2 focus:ring-[#F56753] focus:bg-white transition-all"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-5 py-2 bg-[#F56753] text-white rounded-xl font-bold text-sm hover:bg-[#d94e3a] active:scale-95 transition-all shadow-md shadow-red-650/10 disabled:opacity-50 h-[38px]"
              >
                <FaSearch className="text-xs" />
                Search
              </button>
            </div>
          </form>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100">
            <thead className="bg-slate-50/50">
              <tr>
                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">Type</th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">Booking ID</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center gap-2 text-slate-400">
                      <FaSync className="animate-spin text-2xl text-[#F56753]" />
                      <span className="text-xs font-bold tracking-wider uppercase">Loading Transactions...</span>
                    </div>
                  </td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-slate-400 font-semibold text-sm">
                    No transactions found matching current filters.
                  </td>
                </tr>
              ) : (
                (() => {
                  const getBookingCode = (item) => {
                    if (typeof item.bookingId === 'object' && item.bookingId !== null && item.bookingId.bookingId) {
                      return item.bookingId.bookingId;
                    }
                    if (typeof item.bookingId === 'string' && item.bookingId.startsWith('CB')) {
                      return item.bookingId;
                    }
                    if (item.description) {
                      const match = item.description.match(/\b(CB[A-Z0-9]+)\b/i);
                      if (match) return match[1].toUpperCase();
                    }
                    return "-";
                  };

                  const renderTypeBadge = (type, isCredit) => {
                    let classes = "";
                    let label = type;

                    if (type === 'WALLET_TOPUP' || type === 'ADD_FUNDS') {
                      classes = "bg-emerald-50 text-emerald-700 border border-emerald-100";
                    } else if (type === 'RIDE_DEBIT (Base Fare)') {
                      classes = "bg-rose-50 text-[#F56753] border border-rose-200";
                      label = "Base Fare";
                    } else if (type === 'EXTRA_KM_CHARGE') {
                      classes = "bg-blue-50 text-blue-700 border border-blue-100";
                      label = "Extra Km Charge";
                    } else if (type === 'WAITING_CHARGE') {
                      classes = "bg-purple-50 text-purple-700 border border-purple-100";
                      label = "Waiting Charge";
                    } else if (type === 'RIDE_DEBIT') {
                      classes = "bg-red-50 text-[#F56753] border border-red-100";
                    } else if (type === 'REFUND') {
                      classes = "bg-teal-50 text-teal-700 border border-teal-100";
                    } else {
                      classes = isCredit
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                        : 'bg-red-50 text-[#F56753] border border-red-100';
                    }

                    return (
                      <span className={`text-[10px] font-black px-2.5 py-1 rounded-full ${classes}`}>
                        {label}
                      </span>
                    );
                  };

                  const displayRows = [];
                  transactions.forEach((t) => {
                    const displayBookingId = getBookingCode(t);
                    const isRideDebit = t.type === 'RIDE_DEBIT';

                    if (isRideDebit) {
                      const extraKm = t.extraKmCharge || t.bookingDetails?.extraKmCharge || 0;
                      const waiting = t.waitingCharge || t.bookingDetails?.waitingCharge || 0;
                      const totalAmount = Math.abs(t.amount || 0);
                      const baseFare = Math.max(0, totalAmount - extraKm - waiting);

                      if (extraKm === 0 && waiting === 0) {
                        displayRows.push({
                          ...t,
                          rowKey: `${t._id}-main`,
                          displayType: 'RIDE_DEBIT',
                          displayAmount: t.amount,
                          displayBookingId,
                        });
                      } else {
                        // 1. Base Fare row
                        displayRows.push({
                          ...t,
                          rowKey: `${t._id}-base`,
                          displayType: 'RIDE_DEBIT (Base Fare)',
                          displayAmount: t.isCredit ? baseFare : -baseFare,
                          displayBookingId,
                        });

                        // 2. Extra Km Charge row
                        if (extraKm > 0) {
                          displayRows.push({
                            ...t,
                            rowKey: `${t._id}-extra-km`,
                            displayType: 'EXTRA_KM_CHARGE',
                            displayAmount: t.isCredit ? extraKm : -extraKm,
                            displayBookingId,
                          });
                        }

                        // 3. Waiting Charge row
                        if (waiting > 0) {
                          displayRows.push({
                            ...t,
                            rowKey: `${t._id}-waiting`,
                            displayType: 'WAITING_CHARGE',
                            displayAmount: t.isCredit ? waiting : -waiting,
                            displayBookingId,
                          });
                        }
                      }
                    } else {
                      displayRows.push({
                        ...t,
                        rowKey: t._id,
                        displayType: t.type,
                        displayAmount: t.amount,
                        displayBookingId,
                      });
                    }
                  });

                  return displayRows.map((row) => {
                    return (
                      <tr key={row.rowKey} className="hover:bg-slate-50/80 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-500 font-semibold">
                          {formatDate(row.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            {row.displayType !== row.type && (
                              <span className="text-slate-400 font-black text-sm mr-1">↳</span>
                            )}
                            {renderTypeBadge(row.displayType, row.isCredit)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-black">
                          <span className={row.isCredit ? 'text-emerald-600' : 'text-rose-600'}>
                            {row.isCredit ? '+' : (row.displayAmount < 0 ? '' : '-')}
                            {formatCurrency(Math.abs(row.displayAmount))}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`text-[9px] font-black tracking-wider uppercase px-2 py-0.5 rounded-md ${
                            row.status === 'SUCCESS' 
                              ? 'bg-emerald-50 text-emerald-600' 
                              : row.status === 'PENDING'
                              ? 'bg-amber-50 text-amber-600'
                              : 'bg-rose-50 text-rose-600'
                          }`}>
                            {row.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-slate-700">
                          {row.displayBookingId !== "-" ? (
                            <div className="flex items-center gap-1.5 font-mono text-slate-800 bg-slate-50 px-2 py-1 rounded-md border border-slate-100 w-max shadow-sm">
                              <span className="text-slate-600">{row.displayBookingId}</span>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(row.displayBookingId);
                                  toast.success("Booking ID copied to clipboard!");
                                }}
                                className="hover:text-[#F56753] text-slate-400 p-0.5 transition-colors"
                                title="Copy Booking ID"
                              >
                                <FaClipboardList className="text-[10px]" />
                              </button>
                            </div>
                          ) : (
                            <span className="text-slate-400 font-medium">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  });
                })()
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Request Credit Dialog */}
      {showRequestCredit && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all duration-300">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 max-w-md w-full overflow-hidden transform transition-all animate-scaleUp">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-slate-100">
              <h2 className="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center">
                  <FaPlus className="text-xs" />
                </div>
                Request Line of Credit
              </h2>
              <button
                onClick={() => setShowRequestCredit(false)}
                disabled={requestLoading}
                className="text-slate-400 hover:text-slate-600 transition-colors p-1"
              >
                <FaTimes size={18} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleRequestCredit} className="p-6 space-y-4">
              {/* Amount */}
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2 px-1">
                  Requested Amount (₹)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 font-black text-slate-400 text-lg">₹</span>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    value={creditRequestAmount}
                    onChange={(e) => setCreditRequestAmount(e.target.value)}
                    placeholder="e.g. 50000"
                    disabled={requestLoading}
                    autoFocus
                    className="w-full pl-9 pr-4 py-3 border border-slate-200 bg-slate-50/50 rounded-xl font-bold focus:outline-none focus:ring-2 focus:ring-[#F56753] focus:bg-white transition-all text-slate-800 text-lg"
                  />
                </div>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2 px-1">
                  Justification / Reason
                </label>
                <textarea
                  value={creditRequestReason}
                  onChange={(e) => setCreditRequestReason(e.target.value)}
                  placeholder="Provide a brief explanation for auditing purposes..."
                  disabled={requestLoading}
                  className="w-full px-4 py-3 border border-slate-200 bg-slate-50/50 rounded-xl font-bold focus:outline-none focus:ring-2 focus:ring-[#F56753] focus:bg-white transition-all text-slate-800 text-sm placeholder:text-slate-300"
                  rows="3"
                />
              </div>

              {/* Info Box */}
              <div className="bg-red-50/55 border border-red-100 rounded-xl p-4">
                <div className="flex gap-3">
                  <span className="text-[#F56753] text-sm mt-0.5">ℹ️</span>
                  <div className="text-xs text-[#F56753] font-semibold leading-relaxed">
                    <p className="font-bold text-red-900 mb-0.5">Approval Flow</p>
                    <p className="text-red-700/80 font-medium">Your request will be submitted to UserBanRide administrators for verification. Approvals are typically processed within 24 hours.</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowRequestCredit(false)}
                  disabled={requestLoading}
                  className="flex-1 py-3 border border-slate-200 text-slate-500 rounded-xl hover:bg-slate-50 disabled:opacity-50 transition-colors font-bold text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={requestLoading || !creditRequestAmount || !creditRequestReason.trim()}
                  className="flex-1 py-3 bg-[#F56753] text-white rounded-xl hover:bg-[#d94e3a] disabled:opacity-50 transition-all font-bold text-sm flex items-center justify-center"
                >
                  {requestLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      Submitting Request...
                    </>
                  ) : (
                    "Submit Request"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quick Topup Dialog - Razorpay */}
      {showQuickTopup && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all duration-300">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 max-w-md w-full overflow-hidden transform transition-all animate-scaleUp">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-slate-100">
              <h2 className="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                  <FaPlus className="text-xs" />
                </div>
                Top Up Wallet via Razorpay
              </h2>
              <button
                onClick={() => setShowQuickTopup(false)}
                disabled={topupLoading}
                className="text-slate-400 hover:text-slate-600 transition-colors p-1"
              >
                <FaTimes size={18} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleQuickTopup} className="p-6 space-y-4">
              {/* Amount */}
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2 px-1">
                  Top-Up Amount (₹)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 font-black text-slate-400 text-lg">₹</span>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    value={quickTopupAmount}
                    onChange={(e) => setQuickTopupAmount(e.target.value)}
                    placeholder="e.g. 5000"
                    disabled={topupLoading}
                    autoFocus
                    className="w-full pl-9 pr-4 py-3 border border-slate-200 bg-slate-50/50 rounded-xl font-bold focus:outline-none focus:ring-2 focus:ring-[#F56753] focus:bg-white transition-all text-slate-800 text-lg"
                  />
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-4">
                <div className="flex gap-3">
                  <span className="text-emerald-600 text-sm mt-0.5">🔒</span>
                  <div className="text-xs text-emerald-800 font-semibold leading-relaxed">
                    <p className="font-bold text-emerald-900 mb-0.5">Secure Gateway Payment</p>
                    <p className="text-emerald-700/80 font-medium">You will be redirected to the secure Razorpay payment wizard to complete this credit transaction.</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowQuickTopup(false)}
                  disabled={topupLoading}
                  className="flex-1 py-3 border border-slate-200 text-slate-500 rounded-xl hover:bg-slate-50 disabled:opacity-50 transition-colors font-bold text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={topupLoading || !quickTopupAmount}
                  className="flex-1 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition-all font-bold text-sm flex items-center justify-center"
                >
                  {topupLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      Contacting Gateway...
                    </>
                  ) : (
                    "Authorize & Pay"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorporateWallet;
